import type { BusinessEdge, BusinessFlow, BusinessNode } from './business-logic-types.js';

// Presentation coordinates only. Conditions, transitions and node content remain
// exclusively in business-logic-data; this module cannot change caller behaviour.
export type Point = { x: number; y: number };
export type Bounds = { left: number; top: number; right: number; bottom: number };
export type NodeBox = Bounds & { cx: number; cy: number };
export type EdgeGeometry = {
  points: Point[];
  path: string;
  label: Point;
  labelBounds: Bounds;
  crossings: (Point & { edgeID: string })[];
};
export type FlowLayout = {
  nodes: Map<string, NodeBox>;
  edges: Map<string, EdgeGeometry>;
  width: number;
  height: number;
};
const GRID = { column: 370, row: 210, width: 252, height: 92, decisionHeight: 116, padding: 86 };
type Side = 'top' | 'right' | 'bottom' | 'left';
type Segment = { a: Point; b: Point; edge: BusinessEdge };
const cache = new WeakMap<BusinessFlow, FlowLayout>();
const epsilon = 0.01;
const close = (a: number, b: number) => Math.abs(a - b) < epsilon;

function presentationColumn(node: BusinessNode, flow?: BusinessFlow) {
  if (flow && ['a', 'b', 'c21'].includes(flow.id) && node.id === 'spelling') return 2;
  if (flow?.id === 'existing' && ['open', 'done'].includes(node.id)) return 1;
  return node.column;
}

export function businessNodeBox(node: BusinessNode, flow?: BusinessFlow): NodeBox {
  const height = node.kind === 'decision' ? GRID.decisionHeight : GRID.height;
  const left = GRID.padding + presentationColumn(node, flow) * GRID.column;
  const top = GRID.padding + node.row * GRID.row + (GRID.height - height) / 2;
  return { left, top, right: left + GRID.width, bottom: top + height, cx: left + GRID.width / 2, cy: top + height / 2 };
}

const intersects = (a: Bounds, b: Bounds, gap = 0) =>
  a.left < b.right + gap && a.right > b.left - gap && a.top < b.bottom + gap && a.bottom > b.top - gap;

function segmentHits(a: Point, b: Point, box: Bounds, gap = 0) {
  return close(a.x, b.x)
    ? a.x > box.left - gap &&
        a.x < box.right + gap &&
        Math.max(a.y, b.y) > box.top - gap &&
        Math.min(a.y, b.y) < box.bottom + gap
    : a.y > box.top - gap &&
        a.y < box.bottom + gap &&
        Math.max(a.x, b.x) > box.left - gap &&
        Math.min(a.x, b.x) < box.right + gap;
}

function overlap(a: Point, b: Point, c: Point, d: Point) {
  if (close(a.x, b.x) && close(c.x, d.x) && close(a.x, c.x))
    return Math.max(
      0,
      Math.min(Math.max(a.y, b.y), Math.max(c.y, d.y)) - Math.max(Math.min(a.y, b.y), Math.min(c.y, d.y)),
    );
  if (close(a.y, b.y) && close(c.y, d.y) && close(a.y, c.y))
    return Math.max(
      0,
      Math.min(Math.max(a.x, b.x), Math.max(c.x, d.x)) - Math.max(Math.min(a.x, b.x), Math.min(c.x, d.x)),
    );
  return 0;
}

function crampedParallelLength(a: Point, b: Point, c: Point, d: Point) {
  const vertical = close(a.x, b.x);
  if (vertical !== close(c.x, d.x)) return 0;
  const distance = vertical ? Math.abs(a.x - c.x) : Math.abs(a.y - c.y);
  if (distance < epsilon || distance >= 14) return 0;
  return vertical
    ? Math.max(0, Math.min(Math.max(a.y, b.y), Math.max(c.y, d.y)) - Math.max(Math.min(a.y, b.y), Math.min(c.y, d.y)))
    : Math.max(0, Math.min(Math.max(a.x, b.x), Math.max(c.x, d.x)) - Math.max(Math.min(a.x, b.x), Math.min(c.x, d.x)));
}

function crossing(a: Point, b: Point, c: Point, d: Point, inclusive = false): Point | undefined {
  const vertical = close(a.x, b.x);
  if (vertical === close(c.x, d.x)) return;
  const v = vertical ? [a, b] : [c, d];
  const h = vertical ? [c, d] : [a, b];
  const margin = inclusive ? -epsilon : epsilon;
  if (
    v[0]!.x > Math.min(h[0]!.x, h[1]!.x) + margin &&
    v[0]!.x < Math.max(h[0]!.x, h[1]!.x) - margin &&
    h[0]!.y > Math.min(v[0]!.y, v[1]!.y) + margin &&
    h[0]!.y < Math.max(v[0]!.y, v[1]!.y) - margin
  )
    return { x: v[0]!.x, y: h[0]!.y };
}

function simplify(points: Point[]) {
  const unique = points.filter((p, i) => !i || !close(p.x, points[i - 1]!.x) || !close(p.y, points[i - 1]!.y));
  return unique.filter(
    (p, i) =>
      !i ||
      i === unique.length - 1 ||
      !(
        (close(unique[i - 1]!.x, p.x) && close(unique[i + 1]!.x, p.x)) ||
        (close(unique[i - 1]!.y, p.y) && close(unique[i + 1]!.y, p.y))
      ),
  );
}

function sides(edge: BusinessEdge, nodes: Map<string, NodeBox>): [Side, Side] {
  const a = nodes.get(edge.from)!;
  const b = nodes.get(edge.to)!;
  if (edge.from === 'spelling' && edge.to === 'verified') return ['left', 'top'];
  if (edge.from === 'email' && (edge.to === 'verified' || edge.to === 'ready')) return ['bottom', 'right'];
  if (close(a.cy, b.cy)) return ['bottom', 'bottom'];
  if (close(a.cx, b.cx)) return b.cy > a.cy ? ['bottom', 'top'] : ['right', 'right'];
  const direction = b.cx > a.cx;
  // Outward branches leave the spine sideways; successful returns enter its
  // side, making the true merge visible instead of sharing the main rail.
  if (b.cy > a.cy && edge.to !== 'help' && a.cx === GRID.padding + GRID.column + GRID.width / 2)
    return [direction ? 'right' : 'left', 'top'];
  return [direction ? 'right' : 'left', direction ? 'left' : 'right'];
}

function port(box: NodeBox, side: Side, offset: number): Point {
  if (side === 'top' || side === 'bottom') return { x: box.cx + offset, y: side === 'top' ? box.top : box.bottom };
  return { x: side === 'left' ? box.left : box.right, y: box.cy + offset };
}
function outside(point: Point, side: Side, length: number): Point {
  return {
    x: point.x + (side === 'right' ? length : side === 'left' ? -length : 0),
    y: point.y + (side === 'bottom' ? length : side === 'top' ? -length : 0),
  };
}

class Heap {
  private values: { key: number; score: number }[] = [];
  push(value: { key: number; score: number }) {
    let i = this.values.length;
    this.values.push(value);
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (this.values[p]!.score <= value.score) break;
      this.values[i] = this.values[p]!;
      i = p;
    }
    this.values[i] = value;
  }
  pop() {
    const first = this.values[0];
    const last = this.values.pop();
    if (this.values.length && last) {
      let i = 0;
      while (true) {
        let child = i * 2 + 1;
        if (child >= this.values.length) break;
        if (child + 1 < this.values.length && this.values[child + 1]!.score < this.values[child]!.score) child++;
        if (this.values[child]!.score >= last.score) break;
        this.values[i] = this.values[child]!;
        i = child;
      }
      this.values[i] = last;
    }
    return first;
  }
}

function route(
  edge: BusinessEdge,
  start: Point,
  finish: Point,
  a: Point,
  b: Point,
  boxes: NodeBox[],
  rails: Segment[],
  width: number,
  height: number,
): Point[] {
  const xs = new Set<number>([a.x, b.x, start.x, finish.x]);
  const ys = new Set<number>([a.y, b.y, start.y, finish.y]);
  for (let x = 22; x < width; x += 22) xs.add(x);
  for (let y = 22; y < height; y += 22) ys.add(y);
  for (const box of boxes) {
    [box.left - 22, box.right + 22, box.cx].forEach(x => xs.add(x));
    [box.top - 22, box.bottom + 22, box.cy].forEach(y => ys.add(y));
  }
  const xx = [...xs].sort((a, b) => a - b),
    yy = [...ys].sort((a, b) => a - b);
  const nx = xx.length,
    ny = yy.length;
  const index = (p: Point) => yy.indexOf(p.y) * nx + xx.indexOf(p.x);
  const point = (n: number) => ({ x: xx[n % nx]!, y: yy[Math.floor(n / nx)]! });
  const origin = index(a),
    target = index(b);
  const distances = new Map<number, number>();
  const previous = new Map<number, number>();
  const heap = new Heap();
  const originKey = origin * 3 + 2;
  distances.set(originKey, 0);
  heap.push({ key: originKey, score: 0 });
  const nearSharedPort = (p: Point, q: Point, rail: Segment) => {
    if (edge.from === rail.edge.from) return [p, q].every(v => Math.abs(v.x - start.x) + Math.abs(v.y - start.y) <= 24);
    if (edge.to === rail.edge.to) return [p, q].every(v => Math.abs(v.x - finish.x) + Math.abs(v.y - finish.y) <= 24);
    return false;
  };
  let found: number | undefined;
  while (true) {
    const current = heap.pop();
    if (!current) break;
    const n = Math.floor(current.key / 3),
      direction = current.key % 3;
    const p = point(n),
      distance = distances.get(current.key)!;
    const heuristic = Math.abs(p.x - b.x) + Math.abs(p.y - b.y);
    if (current.score > distance + heuristic + epsilon && current.key !== originKey) continue;
    if (n === target) {
      found = current.key;
      break;
    }
    const x = n % nx,
      y = Math.floor(n / nx);
    for (const [next, axis] of [
      [x ? n - 1 : -1, 0],
      [x + 1 < nx ? n + 1 : -1, 0],
      [y ? n - nx : -1, 1],
      [y + 1 < ny ? n + nx : -1, 1],
    ]) {
      if (next! < 0) continue;
      const q = point(next!);
      if (boxes.some(box => segmentHits(p, q, box, 7))) continue;
      if (rails.some(rail => overlap(p, q, rail.a, rail.b) > epsilon && !nearSharedPort(p, q, rail))) continue;
      const contacts = rails.flatMap(rail => {
        const at = crossing(p, q, rail.a, rail.b, true);
        return at && !nearSharedPort(at, at, rail) ? [{ at, rail }] : [];
      });
      // A connector may cross a straight rail, but cannot turn on it or touch
      // an unrelated bend. Those contacts look like an invented graph merge.
      if (
        contacts.some(
          ({ at, rail }) =>
            (close(at.x, rail.a.x) && close(at.y, rail.a.y)) ||
            (close(at.x, rail.b.x) && close(at.y, rail.b.y)) ||
            (direction !== 2 && direction !== axis && close(at.x, p.x) && close(at.y, p.y)),
        )
      )
        continue;
      const intersections = contacts.length;
      // Distinct rails must remain visually distinct at the fitted zoom. A
      // strong length-weighted cost prevents nearly coincident 4–5px lanes,
      // while allowing the short fan-out immediately outside a shared port.
      const cramped = rails.reduce(
        (sum, rail) => sum + (nearSharedPort(p, q, rail) ? 0 : crampedParallelLength(p, q, rail.a, rail.b)),
        0,
      );
      const nextDistance =
        distance +
        Math.abs(p.x - q.x) +
        Math.abs(p.y - q.y) +
        (direction !== 2 && direction !== axis ? 32 : 0) +
        intersections * 95 +
        cramped * 30;
      const key = next! * 3 + axis!;
      if (nextDistance >= (distances.get(key) ?? Infinity)) continue;
      distances.set(key, nextDistance);
      previous.set(key, current.key);
      heap.push({ key, score: nextDistance + Math.abs(q.x - b.x) + Math.abs(q.y - b.y) });
    }
  }
  if (found === undefined) throw new Error(`Business Logic presentation could not route ${edge.id}`);
  const points: Point[] = [];
  let key: number | undefined = found;
  while (key !== undefined) {
    points.push(point(Math.floor(key / 3)));
    key = previous.get(key);
  }
  return simplify([start, ...points.reverse(), finish]);
}

function labelSizes(edge: BusinessEdge) {
  const strings = edge.label ? [edge.label.de, edge.label.en] : [];
  const length = Math.max(0, ...strings.map(value => value.length));
  return [180, 140, 112, 96].map(max => {
    const width = length ? Math.min(max, length * 6.4 + 18) : 0;
    const lineCount = (value: string) => {
      let lines = 1,
        line = 0;
      for (const word of value.split(/\s+/)) {
        const wordWidth = word.length * 6.4;
        if (line && line + wordWidth + 6.4 > width - 18) {
          lines++;
          line = 0;
        }
        if (wordWidth > width - 18) {
          lines += Math.ceil(wordWidth / (width - 18)) - 1;
        }
        line += wordWidth + (line ? 6.4 : 0);
      }
      return lines;
    };
    return { width, height: length ? Math.max(...strings.map(lineCount)) * 14.5 + 12 : 0 };
  });
}

export function businessFlowLayout(flow: BusinessFlow): FlowLayout {
  const existing = cache.get(flow);
  if (existing) return existing;
  const nodes = new Map(flow.nodes.map(node => [node.id, businessNodeBox(node, flow)]));
  const boxes = [...nodes.values()];
  let width = Math.max(...boxes.map(box => box.right)) + GRID.padding;
  let height = Math.max(...boxes.map(box => box.bottom)) + GRID.padding;
  const edges = new Map<string, EdgeGeometry>();
  const rails: Segment[] = [];
  const ordered = [...flow.edges].sort((left, right) => {
    const priority = (e: BusinessEdge) => {
      const a = nodes.get(e.from)!,
        b = nodes.get(e.to)!;
      return close(a.cx, b.cx) && b.cy > a.cy ? 0 : e.to === 'help' ? 2 : 1;
    };
    return priority(left) - priority(right);
  });
  const counts = new Map<string, number>();
  const offsets = [0, 24, -24, 40, -40];
  for (const edge of ordered) {
    const [fromSide, toSide] = sides(edge, nodes);
    const allocate = (id: string, side: Side, mode: string) => {
      const key = `${id}:${side}:${mode}`;
      const count = counts.get(key) ?? 0;
      counts.set(key, count + 1);
      return flow.nodes.find(node => node.id === id)!.kind === 'decision' ? 0 : offsets[count % offsets.length]!;
    };
    const start = port(nodes.get(edge.from)!, fromSide, allocate(edge.from, fromSide, 'out'));
    const finish = port(nodes.get(edge.to)!, toSide, allocate(edge.to, toSide, 'in'));
    const a = outside(start, fromSide, 22),
      b = outside(finish, toSide, 22);
    const points = route(edge, start, finish, a, b, boxes, rails, width, height);
    edges.set(edge.id, {
      points,
      path: points.map((p, i) => `${i ? 'L' : 'M'} ${p.x} ${p.y}`).join(' '),
      label: { x: 0, y: 0 },
      labelBounds: { left: 0, top: 0, right: 0, bottom: 0 },
      crossings: [],
    });
    points.slice(1).forEach((b, i) => rails.push({ a: points[i]!, b, edge }));
  }
  // Every perpendicular crossing gets a visible white bridge underneath the
  // later stroke. No crossing implies a decision or a merge.
  const seen: Segment[] = [];
  for (const edge of ordered) {
    const geometry = edges.get(edge.id)!;
    geometry.points.slice(1).forEach((b, i) => {
      const a = geometry.points[i]!;
      for (const previous of seen) {
        const p = crossing(a, b, previous.a, previous.b);
        if (p) geometry.crossings.push({ ...p, edgeID: previous.edge.id });
      }
      seen.push({ a, b, edge });
    });
  }
  const reserved: Bounds[] = [];
  for (const edge of ordered) {
    if (!edge.label) continue;
    const geometry = edges.get(edge.id)!;
    const candidates: { label: Point; labelBounds: Bounds; cost: number }[] = [];
    for (const [sizeRank, size] of labelSizes(edge).entries())
      geometry.points.slice(1).forEach((b, i) => {
        const a = geometry.points[i]!,
          vertical = close(a.x, b.x);
        const length = Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
        if (length < 32) return;
        for (const fraction of Array.from({ length: 19 }, (_, index) => (index + 1) / 20)) {
          for (const offset of [0, -1, 1]) {
            const label = {
              x: a.x + (b.x - a.x) * fraction + (vertical ? offset * (size.width / 2 + 9) : 0),
              y: a.y + (b.y - a.y) * fraction + (vertical ? 0 : offset * (size.height / 2 + 7)),
            };
            const labelBounds = {
              left: label.x - size.width / 2,
              top: label.y - size.height / 2,
              right: label.x + size.width / 2,
              bottom: label.y + size.height / 2,
            };
            if (
              labelBounds.left < 4 ||
              labelBounds.top < 4 ||
              labelBounds.right > width - 4 ||
              labelBounds.bottom > height - 4
            )
              continue;
            if (
              boxes.some(box => intersects(labelBounds, box, 9)) ||
              reserved.some(box => intersects(labelBounds, box, 7))
            )
              continue;
            if (rails.some(rail => rail.edge.id !== edge.id && segmentHits(rail.a, rail.b, labelBounds, 7))) continue;
            candidates.push({
              label,
              labelBounds,
              cost:
                sizeRank * 70 +
                i * 8 +
                Math.abs(offset) * 15 +
                Math.abs(fraction - 0.5) * 10 -
                Math.min(length, 200) / 20,
            });
          }
        }
      });
    candidates.sort((a, b) => a.cost - b.cost);
    if (!candidates.length) throw new Error(`Business Logic presentation has no clear label position for ${edge.id}`);
    Object.assign(geometry, candidates[0]!);
    reserved.push(geometry.labelBounds);
  }
  const result = { nodes, edges, width, height };
  cache.set(flow, result);
  return result;
}

export function businessFlowGeometry(flow: BusinessFlow) {
  return businessFlowLayout(flow).edges;
}
export function businessEdgeGeometry(flow: BusinessFlow, edge: BusinessEdge) {
  return businessFlowLayout(flow).edges.get(edge.id)!;
}
